/* Home Pública institucional — vitrine do observatório prospectivo.
 * Redesenho Fev/2026:
 *   - Hero textual mais elegante, sem CTA "Ver Mapa Público" (removido)
 *   - Mapa promovido a protagonista absoluto da página (PublicHomeMap)
 *   - Tipografia refinada com hierarquia clara: selo · título · subtítulo · descrição
 *   - Espaços em branco recalibrados, composição estratégica/institucional
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Globe2, FileText, Sparkles, TrendingUp, MapPin, Activity } from "lucide-react";
import PublicHomeMap from "@/components/PublicHomeMap";
import PublicNewsRadar from "@/components/PublicNewsRadar";
import DataSourceBadge from "@/components/DataSourceBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public`;
const fmtNum = (n) => new Intl.NumberFormat("pt-BR").format(n || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");

export default function PublicHome() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/overview`).then((r) => setData(r.data)).catch(() => setData({}));
  }, []);
  const v = data || {};

  return (
    <div data-testid="public-home">
      {/* ============================================================
         HERO INSTITUCIONAL — texto + Indicadores do Observatório
         ============================================================ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50/60 to-white border-b border-slate-200">
        {/* Padrão de fundo sutil */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #0a2342 1px, transparent 0)", backgroundSize: "28px 28px" }} />
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#0a2342]/[0.06] blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-6 md:pt-10 pb-12 md:pb-16">
          <div className="grid lg:grid-cols-[1.55fr,1fr] gap-10 lg:gap-14 items-center">
            {/* Coluna textual */}
            <div>
              <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-wide uppercase text-amber-700 bg-amber-50/70 border border-amber-200/80 px-3.5 py-1.5 rounded-full mb-6 backdrop-blur-sm">
                <Sparkles size={11} /> LSC · EGN · Escola de Guerra Naval
              </div>

              <h1 className="font-display font-black text-[1.95rem] sm:text-[2.4rem] md:text-[2.6rem] lg:text-[3.4rem] xl:text-[4.2rem] text-[#0a2342] leading-[1.05] tracking-[-0.03em] mb-5 lg:whitespace-nowrap">
                Projeto{" "}
                <span className="text-[#0a2342] relative inline-block">
                  Nostradamus
                  <span className="absolute -bottom-1 left-0 right-0 h-[6px] bg-amber-400/70" style={{ transform: "skewX(-12deg)" }} />
                </span>
              </h1>

              <div
                className="font-display font-semibold text-[#0a2342] text-base md:text-[17px] tracking-tight mb-5"
                data-testid="hero-institutional-line"
              >
                Observatório Prospectivo
                <span className="mx-2 text-amber-500/80">·</span>
                Laboratório de Simulações e Cenários
                <span className="mx-2 text-amber-500/80">·</span>
                EGN
                <span className="mx-2 text-amber-500/80">·</span>
                MB
              </div>

              <h2 className="text-lg md:text-xl text-slate-700 font-light leading-snug max-w-2xl mb-5 tracking-tight">
                Observatório de <span className="font-medium text-[#0a2342]">Sementes de Futuro</span> em
                Segurança Pública &amp; Defesa
              </h2>

              <p className="text-[15px] md:text-base text-slate-600 leading-[1.7] max-w-2xl">
                Monitoramento de sinais emergentes, publicações científicas, relatórios estratégicos e literatura
                institucional relacionados aos futuros da <strong className="text-[#0a2342] font-semibold">Segurança Pública</strong>,
                <strong className="text-[#0a2342] font-semibold"> Segurança Internacional</strong> e <strong className="text-[#0a2342] font-semibold">Defesa</strong>,
                com horizonte prospectivo superior a 2030.
              </p>

              {/* Linha decorativa institucional */}
              <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-wide font-semibold text-slate-400">
                <span className="h-px w-12 bg-amber-400/70" />
                <span>Foresight · Strategic Studies · Defence</span>
              </div>
            </div>

            {/* Card de Indicadores do Observatório */}
            <aside
              className="relative bg-white border border-slate-200/80 rounded-2xl p-6 lg:p-7 shadow-[0_10px_40px_-15px_rgba(10,35,66,0.22)]"
              data-testid="hero-kpis"
            >
              <div className="absolute -top-3 left-6">
                <DataSourceBadge
                  mode={v.data_source_mode}
                  shortLabel={v.data_source_short_label}
                  note={v.methodology_note}
                />
              </div>

              <div className="mt-2 flex items-center justify-between mb-5">
                <div className="flex items-center gap-2 text-slate-600 text-xs uppercase tracking-wide font-semibold">
                  <TrendingUp size={12} className="text-amber-600" /> Indicadores do Observatório
                </div>
                <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">v2.0</span>
              </div>

              <KpiLine label="Documentos monitorados" value={fmtNum(v.total_documents)} accent xl />
              <KpiLine label="Países acompanhados" value={fmtNum(v.total_countries)} />
              <KpiLine label="Novas publicações (7d)" value={fmtNum(v.total_last_7_days)} />
              <KpiLine label="Vetor dominante" value={v.dominant_area_label || "—"} highlight />
              <KpiLine label="Maior volume" value={v.top_country || "—"} highlight />

              <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                <span>Atualizado em {fmtDate(v.last_updated_at)}</span>
                <span className="font-medium">{v.data_source_short_label || "Base monitorada"}</span>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ============================================================
         MAPA PÚBLICO — Protagonista visual da home
         ============================================================ */}
      <section className="relative bg-gradient-to-b from-slate-50/80 to-white py-14 md:py-16" data-testid="public-home-map-section">
        {/* Linha decorativa superior */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-amber-400 to-transparent" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-8 max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-amber-700 mb-3">
              <Activity size={11} /> Monitoramento agregado · Atualização periódica
            </div>
            <h2 className="font-display font-black text-3xl md:text-4xl lg:text-[2.7rem] text-[#0a2342] leading-[1.05] tracking-[-0.02em] mb-3">
              Onde o futuro está sendo escrito
            </h2>
            <p className="text-[15px] md:text-base text-slate-600 leading-relaxed">
              Distribuição geográfica das publicações científicas, estratégicas e institucionais
              monitoradas pelo observatório — consolidadas periodicamente a partir da base interna do Projeto Nostradamus.
            </p>
          </div>

          <PublicHomeMap />
        </div>
      </section>

      {/* ============================================================
         RADAR DE NOTÍCIAS DOS VETORES LSC
         ============================================================ */}
      <PublicNewsRadar />

      {/* ============================================================
         VETORES OFICIAIS
         ============================================================ */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-14 md:py-16">
        <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide font-semibold text-amber-700 mb-2">9 vetores oficiais</div>
            <h3 className="font-display font-black text-3xl md:text-[2.2rem] text-[#0a2342] tracking-[-0.02em] leading-tight">
              Vetores de Pesquisa Prospectiva
            </h3>
          </div>
          <Link
            to="/observatorio"
            className="text-[12.5px] font-semibold text-[#0a2342] hover:text-amber-700 inline-flex items-center gap-1.5 border-b border-transparent hover:border-amber-400 pb-0.5 transition tracking-wide"
          >
            Ver indicadores completos →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5" data-testid="vetores-grid">
          {(v.vetores || []).map((vet) => {
            const count = (v.area_distribution || {})[vet.code] || 0;
            const isDominant = v.dominant_area === vet.code;
            return (
              <div
                key={vet.code}
                className={`relative p-5 rounded-xl border transition-all duration-300 group ${
                  isDominant
                    ? "bg-gradient-to-br from-amber-50 to-amber-50/40 border-amber-300/80 shadow-[0_4px_20px_-8px_rgba(245,158,11,0.35)]"
                    : "bg-white border-slate-200/80 hover:border-[#0a2342]/30 hover:shadow-[0_4px_16px_-8px_rgba(10,35,66,0.18)] hover:-translate-y-0.5"
                }`}
              >
                {isDominant && (
                  <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                    Dominante
                  </span>
                )}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-[#0a2342] text-[15px] leading-snug tracking-tight">
                      {vet.label}
                    </div>
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400 mt-2">
                      {vet.code}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display font-black text-3xl leading-none tabular-nums ${isDominant ? "text-amber-700" : "text-[#0a2342]"}`}>
                      {count}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============================================================
         STRIP DE CHAMADAS INSTITUCIONAIS
         ============================================================ */}
      <section className="bg-[#0a2342] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fbbf24 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-2 gap-10">
          <ColCallout icon={Globe2} title="Mapa Geográfico Público"
                      desc="Visualize onde estão sendo produzidos estudos prospectivos no mundo, com filtros por vetor e detalhamento por país."
                      to="/mapa-publico" cta="Abrir mapa" />
          <ColCallout icon={FileText} title="Observatório de Tendências"
                      desc="Acompanhe a distribuição global por vetor temático e o ranking de países com maior produção monitorada."
                      to="/observatorio" cta="Ver indicadores" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10 text-center">
        <p className="text-xs text-slate-500 max-w-3xl mx-auto leading-relaxed">
          Os dados exibidos representam monitoramento agregado de publicações científicas, estratégicas e institucionais
          relacionadas aos vetores de pesquisa do Projeto Nostradamus. As informações são atualizadas periodicamente
          e podem conter dados simulados em ambiente de demonstração.
        </p>
      </section>
    </div>
  );
}

function KpiLine({ label, value, xl, highlight, accent }) {
  const valueClass = xl
    ? "text-2xl font-black"
    : highlight
    ? "text-[15px] font-bold"
    : "text-xl font-black";
  const colorClass = accent
    ? "text-amber-600"
    : highlight
    ? "text-[#0a2342]"
    : "text-[#0a2342]";
  return (
    <div className="flex items-baseline justify-between gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <span className="text-[12.5px] text-slate-500 tracking-tight">{label}</span>
      <span className={`font-display tracking-tight text-right ${valueClass} ${colorClass}`}>{value}</span>
    </div>
  );
}

function ColCallout({ icon: Icon, title, desc, to, cta }) {
  return (
    <div>
      <Icon size={22} className="text-amber-400 mb-3" />
      <h4 className="font-display font-bold text-xl mb-2 tracking-tight">{title}</h4>
      <p className="text-sm text-slate-300 leading-relaxed mb-4 max-w-md">{desc}</p>
      <Link
        to={to}
        className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 text-xs font-semibold tracking-wide uppercase border-b border-amber-400/40 hover:border-amber-300 pb-0.5 transition"
      >
        <MapPin size={11} /> {cta} →
      </Link>
    </div>
  );
}
